// First-party only. Importing this module never starts network traffic.
import schema from '../../shared/analytics-schema.json'
const KEY = 'zoya-analytics-v1'
let enabled = false, initialized = false, owner = null, queue = [], timer, sending = false
let session = null, lastActivity = 0, assignment = null, generation = 0
const native = !!window.Capacitor?.isNativePlatform?.()
const base = native ? (import.meta.env.VITE_NATIVE_API_URL || 'https://zoya.zozhnik.ru/api') : '/api'
const platform = native ? window.Capacitor.getPlatform() : 'web'
const uuid = () => crypto.randomUUID()
const aliases = { user_registered:'signup_completed', user_logged_in:'login_completed' }
const read = (key) => { try { return localStorage.getItem(key) } catch { return null } }
function persist() { try { localStorage.setItem(KEY, JSON.stringify({owner,queue})) } catch {} }
function token() {
  try { return JSON.parse(read('zoya-user'))?.id === owner ? read('zoya-token') : null } catch { return null }
}
async function request(path, options = {}, timeout = 2500) {
  const c = new AbortController(), t = setTimeout(()=>c.abort(), timeout)
  try { return await fetch(base+'/analytics'+path, {...options,signal:c.signal,credentials:'include'}) }
  finally { clearTimeout(t) }
}
function schedule() { if (!timer && enabled) timer=setTimeout(()=>{timer=null;void flush()},5000) }
export async function flush() {
  if(!enabled || sending || !queue.length) return
  const auth = token()
  if(!auth) return
  const batch = queue.slice(0,30), run = generation
  sending = true
  try {
    const r = await request('/events',{method:'POST',keepalive:true,headers:{'Content-Type':'application/json',Authorization:`Bearer ${auth}`},body:JSON.stringify({events:batch})})
    if(run !== generation) return
    if(r.ok || r.status===400) { const ids=new Set(batch.map(e=>e.id));queue=queue.filter(e=>!ids.has(e.id));persist() }
    if(r.status===204) {enabled=false;queue=[];persist()}
  } catch {} finally { sending=false;if(queue.length)schedule() }
}
export function track(name, properties = {}) {
  try {
    name = aliases[name] || name
    if(!enabled || !owner || !Object.hasOwn(schema,name)) return
    // Property whitelist applies before storage/network, not only at the API.
    const safe = {}
    for(const key of schema[name]) {
      const v = key==='method' ? (properties.method||properties.auth_method) : properties[key]
      if(typeof v==='number' && Number.isFinite(v)) safe[key]=v
      else if(typeof v==='string' && /^[a-zA-Z0-9_/-]{1,80}$/.test(v)) safe[key]=v
    }
    const now=Date.now()
    if(!session || now-lastActivity>30*60*1000) {
      session=uuid();lastActivity=now
      if(name!=='app_session_started') track('app_session_started')
    }
    lastActivity=now
    queue=queue.filter(e=>now-Date.parse(e.at)<86400000).slice(-199)
    queue.push({id:uuid(),name,at:new Date(now).toISOString(),session_id:session,platform,
      build:typeof __APP_VERSION__ !== 'undefined'?String(__APP_VERSION__):null,properties:safe})
    persist();schedule()
  }catch {} // Storage/UUID/network restrictions must not affect the application.
}
export function identify(user) {
  try {
    const next=user?.is_admin?null:user?.id
    if(next===owner)return
    generation++;owner=next;queue=[];assignment=null;session=null
    const saved=JSON.parse(read(KEY)||'null')
    if(saved?.owner===owner && Array.isArray(saved.queue)) queue=saved.queue.filter(e=>Date.now()-Date.parse(e.at)<86400000).slice(-200)
    persist();if(owner)track('app_session_started')
  }catch {}
}
export function reset() {generation++;owner=null;queue=[];session=null;assignment=null;clearTimeout(timer);timer=null;persist()}
export async function preparePaywall() {
  if(!enabled || !owner) return null
  const auth=token(), run=generation
  if(!auth)return null
  try {
    // No local assignment cache: pausing a test takes effect on the next opening.
    const r=await request('/assignment',{headers:{Authorization:`Bearer ${auth}`}},800)
    const d=r.ok?await r.json():null
    if(run!==generation)return null
    assignment=d?.assignment||null
    return assignment
  }catch{return null}
}
export function newExposureId() {try{return uuid()}catch{return null}}
export async function initAnalytics() {
  if(initialized)return
  initialized=true
  try {
    const r=await request('/config');enabled=r.ok && (await r.json()).enabled===true
    if(!enabled)return
    identify(JSON.parse(read('zoya-user')||'null'))
    if(owner && !session) track('app_session_started')
    window.addEventListener('online',()=>void flush())
    document.addEventListener('visibilitychange',()=>{if(document.visibilityState==='hidden')void flush();else track('app_foregrounded')})
  }catch{}
}
// Retain the old entry point for existing imports.
export const initPostHog=initAnalytics
